<template>
  <v-data-table
    fixed-header
    show-expand
    id="products-live-future"
    class="pt-2 elevation-1 i-data-table"
    item-key="productCode"
    v-model:expanded="expandedProduct"
    :headers="headers"
    :height="tableHeight"
    :loading="loading"
    :items="products"
    :items-per-page="pageSize"
    :page="currentPage"
    :search="search"
  >
    <template #top>
      <v-row class="my-2 mb-sm-4" no-gutters>
        <v-col cols="12" class="my-1 px-3 text-right">
          <v-btn
            rounded
            class="i-btn-block-primary"
            :disabled="!(selectedProducts.length > 0)"
            @click="$emit('edit-publish-date', selectedProducts)"
          >
            Edit Publish Date
          </v-btn>
        </v-col>
      </v-row>
    </template>

    <template #[`header.editDate`]="{ header }">
      {{ header.text }}
      <v-icon
        class="i-tooltip-color pb-1"
        small
        v-tooltip="
          'Tick product(s) to edit the Publish date (Note: Edit of a Live product must be deleted to change Publish date)'
        "
      >
        mdi-help-circle-outline
      </v-icon>
      <br />

      <template v-if="products.length">
        <v-btn
          small
          text
          class="i-btn-text-link c-mini"
          v-if="allProductsSelected"
          @click="unselectAllProducts()"
          >Unselect all</v-btn
        >
        <v-btn
          small
          text
          v-else
          class="i-btn-text-link c-mini"
          @click="selectAllProducts()"
          >Select all</v-btn
        >
      </template>
    </template>

    <template #[`header.variableWeightTargetInKg`]="{ header }">
      {{ header.text }}
      <v-icon
        class="i-tooltip-color pb-1"
        small
        v-tooltip="'Applies only to Variable Weight products'"
      >
        mdi-help-circle-outline
      </v-icon>
    </template>

    <template #[`item.variableWeightTargetInKg`]="{ item }">
      <template v-if="item.variableWeightTargetInKg">{{
        `${item.variableWeightTargetInKg} KG`
      }}</template>
      <template v-else>-</template>
    </template>

    <template #[`item.unitQty`]="{ item }">
      <template v-if="item.variableWeightTargetInKg">-</template>
      <template v-else>{{ item.unitQty }} {{ item.unit }}</template>
    </template>

    <template #[`item.newProduct`]="{ item }">
      <v-icon v-if="!item.live" color="green">mdi-check-circle</v-icon>
      <v-icon v-else color="red">mdi-close-circle</v-icon>
    </template>

    <template #[`item.publishDate`]="{ item }">
      <div
        class="g-bold g-border-radius-s i-status-block-green py-1 px-4 d-inline-block"
      >
        {{ $filters.formatDateTime12h(item.publishDateTime) }}
      </div>
    </template>

    <template #[`item.editDate`]="{ item }">
      <v-checkbox
        density="compact"
        class="table-checkbox"
        v-model="selectedProducts"
        :value="item.productCode"
      >
      </v-checkbox>
    </template>

    <template #[`item.actions`]="{ item }">
      <v-btn
        color="secondary"
        icon
        v-tooltip="{
          content: 'Edit',
          visible: $vuetifyDisplay.mdAndDown ? false : undefined
        }"
        @click="$emit('edit-product', item)"
      >
        <v-icon>mdi-circle-edit-outline</v-icon>
      </v-btn>

      <v-btn
        :color="!item.live && item.wasOrIsLive ? 'orange' : 'error'"
        icon
        v-tooltip="{
          content: !item.live && item.wasOrIsLive ? 'Archive' : 'Delete',
          visible: $vuetifyDisplay.mdAndDown ? false : undefined
        }"
        @click="$emit('delete-product', item)"
      >
        <v-icon>{{
          !item.live && item.wasOrIsLive
            ? 'mdi-archive-outline'
            : 'mdi-delete-outline'
        }}</v-icon>
      </v-btn>
    </template>

    <template v-slot:expanded-item="{ headers, item }">
      <td :colspan="headers.length">
        <i-product-details :item="item" :pricing-regions="pricingRegions" />
      </td>
    </template>
  </v-data-table>
</template>

<script>
import iProductDetails from '../products/ProductDetails'

export default {
  compatConfig: {
    WATCH_ARRAY: false
  },

  components: { iProductDetails },

  emits: [
    'add-product',
    'addProduct',
    'delete-product',
    'edit-product',
    'edit-publish-date',
    'update:search'
  ],

  data: () => ({
    currentPage: 1,
    expandedProduct: [],
    selectedProducts: [],
    headers: [
      { text: '', value: 'data-table-expand' },
      {
        text: 'CATEGORY',
        value: 'categoryProductHierarchy.category',
        align: 'start',
        filterable: true,
        width: '7%'
      },
      {
        text: 'SUB CATEGORY',
        value: 'categoryProductHierarchy.subCategory',
        align: 'start',
        filterable: true,
        width: '7%'
      },
      {
        text: 'SUPPLIER ITEM CODE',
        value: 'supplierItemId',
        align: 'start',
        filterable: true,
        width: '6%'
      },
      {
        text: 'BRAND',
        value: 'brand',
        align: 'start',
        filterable: true,
        width: '5%'
      },
      {
        text: 'PRODUCT DESCRIPTION',
        value: 'shortDescription',
        align: 'start',
        filterable: true
      },
      {
        text: 'CARTON WEIGHT',
        value: 'variableWeightTargetInKg',
        align: 'start',
        filterable: true,
        width: '6%'
      },
      {
        text: 'UNIT SIZE',
        value: 'unitQty',
        align: 'start',
        filterable: true,
        width: '5%'
      },
      {
        text: 'UNITS PER CARTON/PALLET',
        value: 'packageQuantity',
        align: 'start',
        filterable: false,
        class: 'units-per-carton',
        width: '6%'
      },
      {
        text: 'UNIT GTIN',
        value: 'baseUnitGtin',
        align: 'start',
        filterable: false,
        width: '6%'
      },
      {
        text: 'NEW PRODUCT?',
        value: 'newProduct',
        align: 'center',
        sortable: false,
        width: '50'
      },
      {
        text: 'PUBLISH DATE',
        value: 'publishDate',
        align: 'center',
        sortable: false,
        width: '120'
      },
      {
        text: 'EDIT PUBLISH DATE',
        value: 'editDate',
        align: 'center',
        sortable: false,
        width: '115'
      },
      {
        text: 'ACTIONS',
        value: 'actions',
        align: 'center',
        sortable: false,
        width: '95'
      }
    ]
  }),

  props: {
    loading: {
      type: Boolean,
      default: false
    },
    pageSize: {
      type: Number,
      default: -1
    },
    products: {
      type: Array,
      default: () => []
    },
    search: {
      type: String,
      default: ''
    },
    pricingRegions: {
      type: Array,
      default: () => []
    },
    supplierItemId: {
      type: String,
      default: null
    },
    tableHeight: {
      default: 'auto'
    },
    addProductHandler: {
      type: Function,
      required: true
    }
  },

  computed: {
    searchModel: {
      get() {
        return this.search
      },
      set(value) {
        this.$emit('update:search', value)
      }
    },
    allProductsSelected() {
      return (
        this.selectableProducts.length > 0 &&
        this.selectedProducts.length === this.selectableProducts.length
      )
    },

    selectableProducts() {
      return this.products
    }
  },

  methods: {
    expandProduct(products, supplierItemId) {
      const pIndex = products.findIndex(
        (p) => p.supplierItemId === supplierItemId
      )
      this.expandedProduct = [products[pIndex]]
      this.currentPage = Math.floor(pIndex / this.pageSize) + 1

      this.goToProduct(supplierItemId)
    },

    goToProduct(url) {
      // Finds HTML element so that the scroll built-in function can be used
      const element = Object.entries(this.$refs).find(
        (entry) => entry[0] === url.toString()
      )?.[1]

      if (element) element.scrollIntoView({ behavior: 'smooth' })
    },

    selectAllProducts() {
      this.selectedProducts = this.selectableProducts.map(
        (product) => product.productCode
      )
    },

    unselectAllProducts() {
      this.selectedProducts = []
    }
  },

  mounted() {
    if (this.supplierItemId)
      this.expandProduct(this.products, this.supplierItemId)
  },

  watch: {
    products: {
      deep: true,
      handler() {
        this.selectedProducts = []
      }
    }
  }
}
</script>

<style lang="scss" scoped>
#products-live-future {
  th,
  td {
    padding: 0px 10px;
  }
  .table-checkbox {
    max-width: 20px;
    max-height: 40px;
    margin: auto;
  }
  .v-input--selection-controls {
    margin-top: 12px;
    padding-top: 2px;
  }
  .v-input--selection-controls .v-input__slot {
    width: 25px;
    align-self: center;
    margin: 0px;
  }
}

@media only screen and (min-width: 960px) {
  #products-live-future {
    th span {
      font-size: 13px;
    }
  }
}
</style>
<style lang="scss">
#products-live-future {
  .v-data-table__expanded__content #product-details {
    box-sizing: border-box;
    max-width: calc(100% - 32px);
    width: calc(100% - 32px);
  }

  .v-data-table__expanded__content #product-details > .v-row {
    margin-left: 0;
    margin-right: 0;
  }

  .v-data-table__expanded__content #product-details > .v-row > .v-col {
    min-width: 0;
  }

  .v-data-table__expanded__content #product-details .p-zone-prices-table {
    display: block;
    max-width: 100%;
    overflow-x: auto;
  }

  @media only screen and (min-width: 960px) {
    .v-data-table__expanded__content #product-details > .v-row > .v-col {
      flex-basis: 50%;
      max-width: 50%;
      min-width: 0;
    }
  }
}
</style>
